import React, { useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";

const TEAL = "#55c5d0";

const KINDS = [
  { key: "sold", label: "Sold" },
  { key: "for_sale", label: "For sale" },
  { key: "leased", label: "Leased" },
  { key: "for_rent", label: "For rent" },
];

/**
 * Add a comparable by hand.
 *
 * Not an edge case: without a third-party sold feed, the sales an agent knows
 * about are frequently ones the CRM has never seen. This is how those get into
 * the report, so the field set matches what the PDF actually prints.
 */
const ManualComparableModal = ({ defaultKind = "sold", defaultSuburb = "", defaultState = "VIC", onClose, onAdd }) => {
  const [form, setForm] = useState({
    kind: defaultKind,
    full_address: "",
    suburb: defaultSuburb || "",
    state: defaultState || "VIC",
    postcode: "",
    property_type: "",
    bedrooms: "",
    bathrooms: "",
    car_spaces: "",
    land_size: "",
    price: "",
    price_label: "",
    sold_date: "",
  });

  const field = (key) => ({
    value: form[key],
    onChange: (e) => setForm((prev) => ({ ...prev, [key]: e.target.value })),
  });

  const submit = () => {
    if (!form.full_address.trim()) {
      toast.error("Enter the property address.");
      return;
    }
    if (!form.price && !form.price_label.trim()) {
      toast.error("Enter a price, or a label like 'Price withheld'.");
      return;
    }

    const numeric = (value) => (value === "" ? null : Number(value));
    onAdd({
      ...form,
      full_address: form.full_address.trim(),
      bedrooms: numeric(form.bedrooms),
      bathrooms: numeric(form.bathrooms),
      car_spaces: numeric(form.car_spaces),
      land_size: numeric(form.land_size),
      price: numeric(form.price),
      sold_date: form.sold_date || null,
      // No coordinates, so this row sorts last when the list is ranked by
      // distance rather than pretending to be next door.
      latitude: null,
      longitude: null,
      distance_km: null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">Add a comparable</h2>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-gray-50 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Type of comparable</label>
            <div className="flex gap-1">
              {KINDS.map((k) => (
                <button
                  key={k.key}
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, kind: k.key }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                    form.kind === k.key ? "text-white border-transparent" : "border-gray-200 text-gray-600"
                  }`}
                  style={form.kind === k.key ? { backgroundColor: TEAL } : undefined}
                >
                  {k.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Address</label>
            <input type="text" autoFocus {...field("full_address")}
              placeholder="26 Hakatere Street"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Suburb</label>
              <input type="text" {...field("suburb")} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">State</label>
              <input type="text" {...field("state")} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Postcode</label>
              <input type="text" {...field("postcode")} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2">
            {[["bedrooms", "Beds"], ["bathrooms", "Baths"], ["car_spaces", "Car"], ["land_size", "Land m²"]].map(([key, label]) => (
              <div key={key}>
                <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
                <input type="number" min="0" {...field(key)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                {form.kind === "for_rent" || form.kind === "leased" ? "Weekly rent" : "Price"}
              </label>
              <input type="number" min="0" {...field("price")}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                {form.kind === "sold" ? "Sold date" : "Property type"}
              </label>
              {form.kind === "sold" ? (
                <input type="date" {...field("sold_date")}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
              ) : (
                <input type="text" {...field("property_type")} placeholder="house"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Or a label instead of a number
            </label>
            <input type="text" {...field("price_label")} placeholder="Price withheld"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button type="button" onClick={submit}
            className="px-4 py-2 rounded-lg text-white text-sm font-semibold"
            style={{ backgroundColor: TEAL }}>
            Add to report
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualComparableModal;
